/**
 * MapLibre side-effect helper: keeps airspace GeoJSON layers in sync with the
 * currently enabled classes + current map viewport. Fetches on map moveend,
 * debounced. Not a React component.
 */

import type { Map as MLMap, GeoJSONSource, ExpressionSpecification } from "maplibre-gl";

import type { AirspaceClassConfig, AirspaceClassId, Bbox4326 } from "../lib/airspace";
import { AIRSPACE_CLASSES, fetchAirspace } from "../lib/airspace";

const SRC_ID = "__airspace_src__";
const FILL_ID = "__airspace_fill__";
const LINE_HALO_ID = "__airspace_line_halo__";  // dark halo behind the main line for contrast
const LINE_ID = "__airspace_line__";
const LABEL_ID = "__airspace_label__";

export type AirspaceFetchStatus = "idle" | "loading" | "error";

export class AirspaceController {
  private map: MLMap;
  private enabled: Set<AirspaceClassId>;
  private abort: AbortController | null = null;
  private debounceHandle: number | null = null;
  private retryHandle: number | null = null;
  /** Per-class opacity multiplier (0..1). Lets the operator tune each band
   * independently — e.g. dim Class B Mode-C-veil rings to a faint tint
   * while keeping Class D outlines bold. */
  private classOpacity: Record<AirspaceClassId, number> = {
    classB: 0.4, classC: 0.4, classD: 0.4, classE5: 0.4,
  };
  /** Optional status callback so the UI can show loading/error spinners.
   * Set after construction via setStatusListener(). */
  private statusListener: ((s: AirspaceFetchStatus, err?: string) => void) | null = null;

  constructor(map: MLMap, enabled: AirspaceClassId[] = []) {
    this.map = map;
    this.enabled = new Set(enabled);
  }

  setStatusListener(fn: ((s: AirspaceFetchStatus, err?: string) => void) | null): void {
    this.statusListener = fn;
  }

  private _emitStatus(s: AirspaceFetchStatus, err?: string): void {
    this.statusListener?.(s, err);
  }

  /** Set opacity for one class (0..1). Cheap data-driven re-paint; safe to
   * call on every slider tick. */
  setClassOpacity(id: AirspaceClassId, o: number): void {
    this.classOpacity[id] = Math.max(0, Math.min(1, o));
    this._applyOpacity();
  }

  /** Bulk replace all class opacities. */
  setOpacities(map: Partial<Record<AirspaceClassId, number>>): void {
    for (const [k, v] of Object.entries(map)) {
      if (typeof v === "number") {
        this.classOpacity[k as AirspaceClassId] = Math.max(0, Math.min(1, v));
      }
    }
    this._applyOpacity();
  }

  private _applyOpacity(): void {
    if (!this.map.getLayer(FILL_ID)) return;
    // Build `match` expressions keyed on __cls (tagged per-feature by the
    // fetcher) so each class gets its own opacity/width — no data refetch.
    //
    // Widths scaled aggressively (0.75 → 4.25 px) so slider at 100% produces
    // a genuinely sharp outline over a busy viewshed. At 40% default the
    // lines sit ~2 px, already readable without looking cartoonish.
    const fillExpr: ExpressionSpecification = ["match",
      ["get", "__cls"] as ExpressionSpecification,
      "classB", this.classOpacity.classB * 0.35,
      "classC", this.classOpacity.classC * 0.35,
      "classD", this.classOpacity.classD * 0.35,
      "classE5", this.classOpacity.classE5 * 0.35,
      0.35,
    ];
    const lineOpacityExpr: ExpressionSpecification = ["match",
      ["get", "__cls"] as ExpressionSpecification,
      "classB", Math.min(1, this.classOpacity.classB * 1.4),
      "classC", Math.min(1, this.classOpacity.classC * 1.4),
      "classD", Math.min(1, this.classOpacity.classD * 1.4),
      "classE5", Math.min(1, this.classOpacity.classE5 * 1.4),
      1.0,
    ];
    const widthExpr: ExpressionSpecification = ["match",
      ["get", "__cls"] as ExpressionSpecification,
      "classB", 0.75 + this.classOpacity.classB * 3.5,
      "classC", 0.75 + this.classOpacity.classC * 3.5,
      "classD", 0.75 + this.classOpacity.classD * 3.5,
      "classE5", 0.75 + this.classOpacity.classE5 * 3.5,
      2.0,
    ];
    // Halo is always wider than the main line, same opacity driver, lower
    // alpha so the colored line rides brightly on top of it.
    const haloWidthExpr: ExpressionSpecification = ["match",
      ["get", "__cls"] as ExpressionSpecification,
      "classB", 1.5 + this.classOpacity.classB * 4.5,
      "classC", 1.5 + this.classOpacity.classC * 4.5,
      "classD", 1.5 + this.classOpacity.classD * 4.5,
      "classE5", 1.5 + this.classOpacity.classE5 * 4.5,
      3.0,
    ];
    const haloOpacityExpr: ExpressionSpecification = ["match",
      ["get", "__cls"] as ExpressionSpecification,
      "classB", this.classOpacity.classB * 0.75,
      "classC", this.classOpacity.classC * 0.75,
      "classD", this.classOpacity.classD * 0.75,
      "classE5", this.classOpacity.classE5 * 0.75,
      0.6,
    ];

    this.map.setPaintProperty(FILL_ID, "fill-opacity", fillExpr);
    this.map.setPaintProperty(LINE_HALO_ID, "line-opacity", haloOpacityExpr);
    this.map.setPaintProperty(LINE_HALO_ID, "line-width", haloWidthExpr);
    this.map.setPaintProperty(LINE_ID, "line-opacity", lineOpacityExpr);
    this.map.setPaintProperty(LINE_ID, "line-width", widthExpr);
    this.map.setPaintProperty(LABEL_ID, "text-opacity", lineOpacityExpr);
  }

  /** Attach sources/layers to the map. Safe to call multiple times. Defensive
   * cleanup first (remove stale layers/sources from a prior style), then
   * (re)build from scratch — safer than trusting getSource() across setStyle. */
  attach(): void {
    // Always remove the old moveend handler before re-binding, otherwise each
    // basemap swap accumulates a duplicate listener and pans trigger N fetches.
    this.map.off("moveend", this.refresh);
    // Defensive cleanup: if the current style somehow retained our layers/
    // source, tear them down before adding fresh ones. Don't just return
    // early on getSource — that path leaves stale layer refs unpainted after
    // setStyle's diff keeps the source shell but drops its features.
    for (const id of [LABEL_ID, LINE_ID, LINE_HALO_ID, FILL_ID]) {
      if (this.map.getLayer(id)) {
        try { this.map.removeLayer(id); } catch { /* ignore */ }
      }
    }
    if (this.map.getSource(SRC_ID)) {
      try { this.map.removeSource(SRC_ID); } catch { /* ignore */ }
    }
    this.map.addSource(SRC_ID, {
      type: "geojson",
      data: { type: "FeatureCollection", features: [] },
    });
    this.map.addLayer({
      id: FILL_ID,
      type: "fill",
      source: SRC_ID,
      paint: {
        "fill-color": ["coalesce", ["get", "__color"], "#666"],
        "fill-opacity": 0.15,
      },
    });
    // Dark halo behind the main colored line — renders as a slightly
    // thicker dark band that makes the class-colored line pop against any
    // basemap (satellite especially). Without it, outlines get lost against
    // similarly-colored terrain.
    this.map.addLayer({
      id: LINE_HALO_ID,
      type: "line",
      source: SRC_ID,
      paint: {
        "line-color": "#000",
        "line-width": 2.0,
        "line-opacity": 0.7,
      },
    });
    this.map.addLayer({
      id: LINE_ID,
      type: "line",
      source: SRC_ID,
      paint: {
        "line-color": ["coalesce", ["get", "__color"], "#666"],
        "line-width": 1.5,
      },
    });
    this.map.addLayer({
      id: LABEL_ID,
      type: "symbol",
      source: SRC_ID,
      layout: {
        "text-field": ["coalesce", ["get", "NAME"], ["get", "IDENT"], ""],
        "text-size": 11,
        "text-anchor": "center",
        "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
      },
      paint: {
        "text-color": "#fff",
        "text-halo-color": "#000",
        "text-halo-width": 1.2,
      },
      filter: ["has", "NAME"],
    });

    // Apply current opacity to freshly-added layers (basemap-swap re-attach
    // path needs this — opacity is held in instance state, paint properties
    // start at the literal values we set in addLayer above).
    this._applyOpacity();

    this.map.on("moveend", this.refresh);
    console.log("[airspace] attached, enabled=", Array.from(this.enabled));
    // Force a non-debounced fetch right away so the user doesn't see an empty
    // overlay while the 250ms debounce window opens. The previous behavior
    // (only the debounced refresh fired) meant any pan within 250ms of attach
    // could clobber the initial render and leave airspace blank until a
    // manual toggle.
    void this.doFetch();
    // Belt-and-suspenders: on initial mount the map is "loaded" but the
    // viewport hasn't always finished projecting. Fire one more fetch after
    // the map reaches "idle" (all sources/styles/animations settled) so
    // refreshes that hit a transient world-view-bounds state still end up
    // with the correct local airspace once everything is ready. Cheap — at
    // most one extra ArcGIS round trip per attach.
    this.map.once("idle", () => { void this.doFetch(); });
  }

  detach(): void {
    this.map.off("moveend", this.refresh);
    this.abort?.abort();
    for (const id of [LABEL_ID, LINE_ID, LINE_HALO_ID, FILL_ID]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    if (this.map.getSource(SRC_ID)) this.map.removeSource(SRC_ID);
  }

  setEnabled(ids: AirspaceClassId[]): void {
    this.enabled = new Set(ids);
    this.refresh();
  }

  refresh = (): void => {
    if (this.debounceHandle) window.clearTimeout(this.debounceHandle);
    this.debounceHandle = window.setTimeout(() => this.doFetch(), 250);
  };

  private async doFetch(retryAttempt = 0): Promise<void> {
    if (!this.map.getSource(SRC_ID)) return;
    if (this.abort) this.abort.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;

    const b = this.map.getBounds();
    const bbox: Bbox4326 = {
      lon_min: b.getWest(), lat_min: b.getSouth(),
      lon_max: b.getEast(), lat_max: b.getNorth(),
    };

    const enabledConfigs = AIRSPACE_CLASSES.filter((c) => this.enabled.has(c.id));
    if (enabledConfigs.length === 0) {
      (this.map.getSource(SRC_ID) as GeoJSONSource).setData({
        type: "FeatureCollection", features: [],
      });
      this._emitStatus("idle");
      return;
    }

    if (retryAttempt === 0) this._emitStatus("loading");
    console.log("[airspace] fetch start", {
      classes: enabledConfigs.map(c => c.id), bbox,
    });
    try {
      const results = await Promise.all(
        enabledConfigs.map((cls: AirspaceClassConfig) => fetchAirspace(cls, bbox, signal)),
      );
      if (signal.aborted) { console.log("[airspace] fetch aborted"); return; }
      const features = results.flatMap((fc) => fc.features);
      (this.map.getSource(SRC_ID) as GeoJSONSource).setData({
        type: "FeatureCollection", features,
      });
      console.log(`[airspace] fetch ok: ${features.length} features rendered`);
      this._emitStatus("idle");
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      console.warn("Airspace fetch failed:", e);
      // ArcGIS occasionally rate-limits or returns 5xx. One retry after
      // 1.5 sec covers transient failures without hammering the upstream.
      if (retryAttempt === 0) {
        this._emitStatus("loading", `${e?.message ?? e} — retrying`);
        if (this.retryHandle) window.clearTimeout(this.retryHandle);
        this.retryHandle = window.setTimeout(() => {
          void this.doFetch(retryAttempt + 1);
        }, 1500);
      } else {
        this._emitStatus("error", String(e?.message ?? e));
      }
    }
  }
}
